/**
 * Ask's category from the wording of a question: Splits (a group's items and who owes what),
 * Tutoring (students, tutors, lessons) or Money (cards, bank accounts, points). Only a clear
 * winner counts; anything unclear returns null and the chat keeps what it was about.
 */
export type AskCategory = "group" | "tutoring" | "money";

const WORDS: Record<AskCategory, RegExp> = {
  tutoring:
    /\b(tutor\w*|lessons?|sessions?|students?|taught|teach\w*|classes|prepaid|hours? left|booked|calendar|schedule\w*|family|families)\b/gi,
  money:
    /\b(cards?|credit|debit|bank|checking|savings|points|rewards?|statements?|apr|amex|chase|discover|visa|mastercard|capital one|annual fee|cash ?back|membership rewards)\b/gi,
  group:
    /\b(costco|receipts?|items?|split\w*|bought|groceries|owes?|owed|trips?|stores?|ticked|ledger|tax|coupons?)\b/gi,
};

const count = (text: string, re: RegExp) => (text.match(re) ?? []).length;
const has = (text: string, name: string) => {
  const n = name.trim().toLowerCase();
  return n.length > 1 && new RegExp(`\\b${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(text);
};

export function guessCategory(
  question: string,
  names: { groups?: string[]; tutoring?: string[] } = {},
): AskCategory | null {
  const t = question.toLowerCase();
  const score: Record<AskCategory, number> = {
    tutoring: count(t, WORDS.tutoring),
    money: count(t, WORDS.money),
    group: count(t, WORDS.group),
  };
  // A group's name says Splits; a student's or tutor's full name says Tutoring.
  for (const g of names.groups ?? []) if (has(t, g)) score.group += 2;
  for (const p of names.tutoring ?? []) if (p.includes(" ") && has(t, p)) score.tutoring += 1;
  const ranked = (Object.entries(score) as [AskCategory, number][]).sort((a, b) => b[1] - a[1]);
  const [first, second] = ranked;
  return first![1] > 0 && first![1] > second![1] ? first![0] : null;
}
